import {
  CloseButton,
  Flex,
  Modal as MantineModal,
  type ModalProps as MantineModalProps,
  ScrollArea,
  Text,
  Title,
} from "@mantine/core";
import { clsx } from "clsx";
import { type ReactNode, useEffect, useState } from "react";
import styles from "./Modal.module.scss";
import { MODAL_SIZE, MODAL_TRANSITION_PROPS, type ModalSize } from "./modal-tokens";

//------------------------------------------------------------------------------------------------
//                                        TYPES
//------------------------------------------------------------------------------------------------
export interface ModalProps {
  /** Whether the modal is open */
  opened: boolean;
  /** Function to close the modal */
  onClose: () => void;
  /** The title of the modal */
  title?: ReactNode;
  /** Optional description text below the title */
  description?: ReactNode;
  /** The content of the modal */
  children: React.ReactNode;
  /**
   * Footer action buttons. By convention the last button is the primary CTA (bottom-right);
   * additional buttons are optional secondary actions to its left.
   */
  buttons?: React.ReactNode[];
  /** Width token — defaults to `md` (520px). Prefer a token over a raw pixel width. */
  size?: ModalSize;
  /** Whether to show the top-right close button (defaults to true) */
  withCloseButton?: boolean;
  /** Additional class name for the modal */
  className?: string;
  /** Whether to disable the scroll area */
  disableScroll?: boolean;
  /**
   * Drop the content gutter so `children` fill the shell edge to edge. For a body that already
   * draws its own card surface (e.g. `AppointmentDetailPopup`) — otherwise it renders as a card
   * nested inside the modal.
   */
  disablePadding?: boolean;
  /** Additional props to pass to the Mantine modal */
  mantineModalProps?: Omit<
    MantineModalProps,
    "opened" | "onClose" | "className" | "withCloseButton" | "size"
  >;
}

//------------------------------------------------------------------------------------------------
//                                        COMPONENT
//------------------------------------------------------------------------------------------------
export function Modal({
  opened,
  onClose,
  title,
  description,
  children,
  buttons,
  size = "md",
  withCloseButton = true,
  className,
  disableScroll = false,
  disablePadding = false,
  mantineModalProps,
}: ModalProps) {
  // Mantine only plays the enter/exit transition when a *mounted* modal's `opened` flips
  // false↔true. Callers that mount the modal already-open (`{isOpen && <Modal opened />}`) would
  // otherwise skip the entrance — so force the first render closed (via `hasMounted`) to give
  // Mantine the false→true edge, then derive `visible` straight from `opened`. Deriving (rather
  // than syncing in an effect) means `opened={false}` takes effect on the same render, so closing
  // never leaves a stale fully-open paint after the parent clears its state.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  const visible = hasMounted && opened;

  return (
    <MantineModal
      opened={visible}
      onClose={onClose}
      size={MODAL_SIZE[size]}
      withCloseButton={false}
      transitionProps={MODAL_TRANSITION_PROPS}
      className={clsx(styles.Modal, className, {
        [styles["scroll-disabled"]]: disableScroll,
        [styles["padding-disabled"]]: disablePadding,
      })}
      {...mantineModalProps}
    >
      {(title || description || withCloseButton) && (
        <header className={styles.Header}>
          <div className={styles.HeaderText}>
            {title && typeof title === "string" ? (
              <Title order={2} className={styles.Title}>
                {title}
              </Title>
            ) : (
              title
            )}
            {description && typeof description === "string" ? (
              <Text className={styles.Description}>{description}</Text>
            ) : (
              description
            )}
          </div>
          {withCloseButton && (
            <CloseButton onClick={onClose} className={styles.CloseButton} aria-label="Close" />
          )}
        </header>
      )}

      <Flex className={styles.MiddleSection}>
        {disableScroll ? (
          children
        ) : (
          <ScrollArea type="always" h="100%" scrollbarSize={6} className={styles.ScrollArea}>
            {children}
          </ScrollArea>
        )}
      </Flex>

      {!!buttons?.length && <footer className={styles.Footer}>{buttons}</footer>}
    </MantineModal>
  );
}
